import defaultAxiosClient from '../../plugins/axios';
import {toYearAndMonth} from "@/util/DateUtil";
import {extractFileNameAndSave} from "@/util/FormattingUtil";
import moment from "moment";

const initialState = {
    selectedInvoice: {},
    invoices: [],
    downloading: false,
    accountingCosts: [],
    currentlySelectedMonth: moment(),
    confirmedEmails: [],
    selectedInvoiceCreditNoteDate: undefined,
};

const invoice = {
    namespaced: true,
    state() {
        return {...initialState};
    },
    getters: {
        selectedInvoice: state => state.selectedInvoice,
        invoices: state => state.invoices,
        accountingCosts: state => state.accountingCosts,
        currentlySelectedMonth: state => state.currentlySelectedMonth,
        confirmedEmails: state => state.confirmedEmails,
        selectedInvoiceCreditNoteDate: state => state.selectedInvoiceCreditNoteDate
    },
    mutations: {
        resetState(state) {
            Object.assign(state, initialState);
        },
        setSelectedInvoice(state, payload) {
            state.selectedInvoice = payload;
        },
        setInvoices(state, payload) {
            state.invoices = payload;
        },
        flipDownloading(state){
            state.downloading = !state.downloading;
        },
        setConfirmedEmails(state, payload){
            state.confirmedEmails = payload._embedded.confirmedEmailDtoList;
       
        },
        setAccountingCosts(state, payload){
            state.accountingCosts = payload._embedded.accountingCostDtoList;
        },

        initializeCurrentlySelectedMonth(state, payload){
            state.currentlySelectedMonth = moment(payload);
        },
        addMonth(state) {
            state.currentlySelectedMonth = moment(state.currentlySelectedMonth).add(1, "month");
        },
        subtractMonth(state) {
            state.currentlySelectedMonth = moment(state.currentlySelectedMonth).subtract(1, "month");
        },
    },
    actions: {
        fetchAccountingCostsList({commit}){
            return defaultAxiosClient.get('administration/accountingcost/all')
                .then(response => {
                    commit('setAccountingCosts', response.data);})
                .catch(() => commit('resetState'));
        },
        fetchConfirmedEmailsList({commit}){
            return defaultAxiosClient.get('administration/confirmedEmail/true')
                .then(response => {
                    commit('setConfirmedEmails', response.data);})
                .catch(() => commit('resetState'));
        },
        
        calculateInvoice({commit}, selectedTimeSheetInfo) {
            const contractId = selectedTimeSheetInfo.contractId;
            const userId = selectedTimeSheetInfo.userId;
            const yearMonthString = toYearAndMonth(this.state.time.currentlySelectedMonth);
            const createInvoiceFromTimesheetDto = {
                contractId: contractId,
                userId: userId,
                yearMonthString: yearMonthString
            };
            return defaultAxiosClient.post(`/administration/invoice/fromTimesheet`, createInvoiceFromTimesheetDto)
                .then(response => commit('setSelectedInvoice', response.data))
                .catch(() => commit('resetState'));
        },
        downloadSelectedInvoicePDF({commit},invoiceId) {
            let uri = `/administration/invoice/${invoiceId}/download`;

            return defaultAxiosClient.get(uri, {
                responseType: "arraybuffer",
                headers: {
                    'Accept': 'application/pdf'
                }
            }).then((response) => {
                const blob = new Blob([response.data], {type: 'application/pdf'});
                extractFileNameAndSave(response, blob);
                commit('flipDownloading');
            });
        },
        downloadSelectedCreditNotePDF({commit},invoiceId) {
            let uri = `/administration/invoice/${invoiceId}/downloadCreditNote`;

            return defaultAxiosClient.get(uri, {
                responseType: "arraybuffer",
                headers: {
                    'Accept': 'application/pdf'
                }
            }).then((response) => {
                const blob = new Blob([response.data], {type: 'application/pdf'});
                extractFileNameAndSave(response, blob);
                commit('flipDownloading');
            });
        },
        mailInvoiceById({commit}, invoiceId) {
            return defaultAxiosClient.post(`/administration/invoice/${invoiceId}/send`)
                .then(response => commit('setSelectedInvoice', response.data))
                .catch(() => commit('resetState'));
        },
        createCreditNote({state, commit}, creditNoteDate) {
            const invoiceId = state.selectedInvoice.id;
            const config = {headers:{'Content-Type': 'text/plain'}};

            return defaultAxiosClient.post(`/administration/invoice/${invoiceId}/creditnote`, creditNoteDate, config)
                .then(response => commit('setSelectedInvoice', response.data))
                .catch(() => commit('resetState'));
        },
        updateInvoice({state, commit}) {
            const updateInvoiceDto = {
                invoiceDate: state.selectedInvoice.invoiceDate,
                VATPercentage: state.selectedInvoice.vatpercentage,
                contactMail: state.selectedInvoice.contactMail,
                customerBillingDto: {
                    organisationName: state.selectedInvoice.customerBillingDto.organisationName,
                    financialSupervisorReference: state.selectedInvoice.customerBillingDto.financialSupervisorReference,
                    street: state.selectedInvoice.customerBillingDto.street,
                    houseNumber:state.selectedInvoice.customerBillingDto.houseNumber,
                    postalCode: state.selectedInvoice.customerBillingDto.postalCode,
                    city: state.selectedInvoice.customerBillingDto.city,
                    country: state.selectedInvoice.customerBillingDto.country,
                    vat: state.selectedInvoice.customerBillingDto.vat,
                    paymentTerm: state.selectedInvoice.customerBillingDto.paymentTerm,
                    wantsConcatenatedDocuments: state.selectedInvoice.customerBillingDto.wantsConcatenatedDocuments,
                    peppolEndPointDto: {
                        active : state.selectedInvoice.customerBillingDto.peppolEndPointDto.active,
                        number : state.selectedInvoice.customerBillingDto.peppolEndPointDto.number,
                        type : state.selectedInvoice.customerBillingDto.peppolEndPointDto.type
                    }
                },
                freeCostBillingDtos: state.selectedInvoice.freeCosts,
                internalReference: state.selectedInvoice.internalReference,
                externalReference: state.selectedInvoice.externalReference,
                disclaimer: state.selectedInvoice.disclaimer,
                confirmedEmailDtos: state.selectedInvoice.confirmedEmailDtos,
            };
            const invoiceId = state.selectedInvoice.id;
            return defaultAxiosClient.put(`/administration/invoice/${invoiceId}`, updateInvoiceDto)
                .then(response => commit('setSelectedInvoice', response.data));
        },
        fetchInvoices({commit}) {
            const yearMonth = toYearAndMonth(this.state.time.currentlySelectedMonth);
            return defaultAxiosClient.get(`/administration/invoice/${yearMonth}/all`)
                .then(response => {
                    if (response.data._embedded !== undefined)
                        commit('setInvoices', response.data._embedded.invoiceDtoList);
                    else
                        commit('setInvoices', []);
                });
        },
        fetchInvoice({commit}) {
            const contractId = this.state.administrationTimeSheet.fullTimeSheet.contractId;
            const yearMonthString = toYearAndMonth(this.state.administrationTimeSheet.fullTimeSheet.yearMonth);
            return defaultAxiosClient.get(`/administration/invoice/${yearMonthString}/${contractId}/all`)
                .then(response => commit('setSelectedInvoice', response.data))
                .catch(() => commit('resetState'));
        },
        findInvoice({commit}) {
            const contractId = this.state.administrationTimeSheet.currentlySelectedContractId;
            const yearMonthString = toYearAndMonth(this.state.administrationTimeSheet.fullTimeSheet.yearMonth);
            return defaultAxiosClient.get(`/administration/invoice/${yearMonthString}/${contractId}/find`)
                .then(response => {
                    if (response.data) {
                        commit('setSelectedInvoice', response.data);
                    }
                    else {
                        commit('setSelectedInvoice', null);
                    }
                })
                .catch(() => commit('resetState'));
        },
        fetchInvoiceById({commit}, invoiceId) {
            return defaultAxiosClient.get(`/administration/invoice/${invoiceId}`)
                .then(response => commit('setSelectedInvoice', response.data))
                .catch(() => commit('resetState'));
        },
        createCustomInvoice({commit}, createInvoiceDto) {
            return defaultAxiosClient.post(`/administration/invoice`, createInvoiceDto)
                .then(response => commit('setSelectedInvoice', response.data));
        },
        updateInvoiceStatus({commit, state}, status) {
            const invoiceId = state.selectedInvoice.id;
            const updateInvoiceStatusDto = {
                status: status
            };
            return defaultAxiosClient.patch(`/administration/invoice/${invoiceId}/status`,updateInvoiceStatusDto)
                .then(response => commit('setSelectedInvoice', response.data));
        }
    }
};

export default invoice;
